import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Select from "react-select";
import axios from "axios";
import toast from "react-hot-toast";
import { useRequestStore } from "../../../data/stores/loggerStore";

const formSchema = z.object({
  erpModule: z.string().min(1, "Data system is required"),
  deptsFeedDataSystem: z
    .array(z.string())
    .min(1, "Department selection is required"),
  systemsFeedDataSystem: z
    .array(z.string())
    .min(1, "System selection is required"),
  deptReceiveData: z
    .array(z.string())
    .min(1, "Receiving department selection is required"),
  deptSystemReceiveData: z
    .array(z.string())
    .min(1, "Receiving system selection is required"),
  reportProduced: z.string().min(1, "Required"),
});

type FormData = z.infer<typeof formSchema>;

const DataFlowEdit = ({ data, setIsEditing }) => {
  const [dataSystems, setDataSystems] = useState<any[]>([]);
  const [departmentFeedsOptions, setDepartmentFeedsOptions] = useState<any[]>(
    []
  );
  const [deptSystemOptions, setDeptSystemOptions] = useState<any[]>([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingDeptSystems, setLoadingDeptSystems] = useState(false);
  const { dataSystemAll, getDynamicLogger }: any = useRequestStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      erpModule: data?.erpModule || undefined,
      deptsFeedDataSystem: data?.deptsFeedDataSystem || [],
      systemsFeedDataSystem: data?.systemsFeedDataSystem || [],
      deptReceiveData: data?.deptReceiveData || [],
      deptSystemReceiveData: data?.deptSystemReceiveData || [],
      reportProduced: data?.reportProduced || "",
    },
  });

  const deptsFeedDataSystemValue = watch("deptsFeedDataSystem");
  const deptReceiveDataValue = watch("deptReceiveData");

  // Add N/A option to all option lists
  const dataSystemsOptions = [
    { value: "N/A", label: "N/A" },
    ...dataSystems.map((item) => ({
      value: item.title,
      label: item.title,
    })),
  ];

  const departmentFeedsOptionsWithNA = [
    { value: "N/A", label: "N/A" },
    ...departmentFeedsOptions,
  ];

  const deptSystemOptionsWithNA = [
    { value: "N/A", label: "N/A" },
    ...deptSystemOptions,
  ];

  // Handler for select changes that handles N/A logic
  const handleSelectChange = (
    fieldName:
      | "deptsFeedDataSystem"
      | "systemsFeedDataSystem"
      | "deptReceiveData"
      | "deptSystemReceiveData",
    selected: any
  ) => {
    if (!selected) {
      setValue(fieldName, []);
      return;
    }

    const selectedValues = selected.map((opt: any) => opt.value);

    // If N/A is selected, clear all other selections and set to ["N/A"]
    if (selectedValues.includes("N/A")) {
      setValue(fieldName, ["N/A"]);
    }
    // If other options are selected and N/A was previously selected, remove N/A
    else if (selectedValues.length > 0) {
      setValue(
        fieldName,
        selectedValues.filter((val: string) => val !== "N/A")
      );
    }
    // If nothing is selected, set to empty array
    else {
      setValue(fieldName, []);
    }
  };

  // Get current value for display (handles N/A case)
  const getSelectValue = (
    fieldName:
      | "deptsFeedDataSystem"
      | "systemsFeedDataSystem"
      | "deptReceiveData"
      | "deptSystemReceiveData",
    options: any[]
  ) => {
    const fieldValue = watch(fieldName) || [];

    if (fieldValue.includes("N/A")) {
      return options.filter((opt) => opt.value === "N/A");
    }

    return options.filter((opt) => fieldValue.includes(opt.value));
  };

  // ✅ Fetch Data Systems & ERP modules based on deptsFeedDataSystem
  useEffect(() => {
    const fetchDataSystems = async () => {
      try {
        setLoadingSystems(true);
        setLoadingDepartments(true);

        let response;

        if (
          deptsFeedDataSystemValue &&
          deptsFeedDataSystemValue.length > 0 &&
          !deptsFeedDataSystemValue.includes("N/A")
        ) {
          response = await axios.post(
            "/api/v1/data-system/manage-department-system",
            {
              email: data?.profile?.user?.email,
              user: data?.profile?.user?._id,
              systemSelection: [
                { category: "departmentName", data: deptsFeedDataSystemValue },
              ],
            }
          );
        } else {
          response = await axios.get(
            "/api/v1/data-system/manage-department-system",
            {
              params: {
                pagination: "not",
                email: data?.profile?.user?.email,
                user: data?.profile?.user?._id,
                datasystem: data?._id,
              },
            }
          );
        }

        const docs = response?.data?.data?.docs || [];

        const departmentNameCategory = docs.find(
          (item) => item.category === "departmentName"
        );
        setDataSystems(departmentNameCategory?.data || []);

        const erpModuleCategory = docs.find(
          (item) => item.category === "erpModule"
        );
        setDepartmentFeedsOptions(
          erpModuleCategory?.data?.map((d) => ({
            value: d.title,
            label: d.title,
          })) || []
        );
      } catch (error: any) {
        console.error(
          "❌ Error fetching data systems:",
          error.response?.data || error.message
        );
      } finally {
        setLoadingSystems(false);
        setLoadingDepartments(false);
      }
    };

    if (data) fetchDataSystems();
  }, [data, deptsFeedDataSystemValue]);

  // ✅ Fetch Systems that deptReceiveData can send to
  useEffect(() => {
    const fetchDeptSystems = async () => {
      try {
        setLoadingDeptSystems(true);

        let response;
        if (
          deptReceiveDataValue &&
          deptReceiveDataValue.length > 0 &&
          !deptReceiveDataValue.includes("N/A")
        ) {
          response = await axios.post(
            "/api/v1/data-system/manage-department-system",
            {
              email: data?.profile?.user?.email,
              user: data?.profile?.user?._id,
              systemSelection: [
                { category: "departmentName", data: deptReceiveDataValue },
              ],
            }
          );
        }

        if (response) {
          const docs = response?.data?.data?.docs || [];
          const erpModuleCategory = docs.find(
            (item) => item.category === "erpModule"
          );

          setDeptSystemOptions(
            erpModuleCategory?.data?.map((d) => ({
              value: d.title,
              label: d.title,
            })) || []
          );
        } else {
          setDeptSystemOptions([]);
        }
      } catch (error: any) {
        console.error(
          "❌ Error fetching dept systems:",
          error.response?.data || error.message
        );
      } finally {
        setLoadingDeptSystems(false);
      }
    };

    if (
      deptReceiveDataValue &&
      deptReceiveDataValue.length > 0 &&
      !deptReceiveDataValue.includes("N/A")
    ) {
      fetchDeptSystems();
    } else {
      setDeptSystemOptions([]);
    }
  }, [deptReceiveDataValue, data]);

  const onSubmit = async (itemForm: any) => {
    try {
      const res = await axios.put(`/api/v1/data-system`, {
        datasystems: [{ ...itemForm, _id: data?._id }],
        email: data.profile.user.email,
        user: data.profile?.user?._id,
      });
      const updatedArray = res?.data?.data;

      const newDocs = dataSystemAll.docs.map((d: any) => {
        const updated = updatedArray.find((u: any) => u._id === d._id);
        if (!updated) return d;
        return {
          ...d,
          ...updated,
          profile:
            typeof updated.profile === "string" ? d.profile : updated.profile,
        };
      });
      const updatedDataSystemAll = { ...dataSystemAll, docs: newDocs };
      getDynamicLogger(updatedDataSystemAll, "dataSystemAll");
      toast.success(
        res?.data?.message ||
          "Data system updated, Changes would appear shortly."
      );
      // toast.success("Data system updated, Changes would appear shortly.");
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className="font-Inter max-w-2xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Department feeds data to system */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which department feeds data to this system{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="deptsFeedDataSystem"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingSystems}
                options={dataSystemsOptions}
                placeholder="Select department(s)..."
                onChange={(selected) =>
                  handleSelectChange("deptsFeedDataSystem", selected)
                }
                value={getSelectValue(
                  "deptsFeedDataSystem",
                  dataSystemsOptions
                )}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.deptsFeedDataSystem && (
            <p className="text-red-500 text-xs mt-1">
              {errors.deptsFeedDataSystem.message}
            </p>
          )}
        </div>

        {/* System feeds data to this system */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which system feeds data to this system{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemsFeedDataSystem"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingDepartments}
                options={departmentFeedsOptionsWithNA}
                placeholder="Select system(s)..."
                onChange={(selected) =>
                  handleSelectChange("systemsFeedDataSystem", selected)
                }
                value={getSelectValue(
                  "systemsFeedDataSystem",
                  departmentFeedsOptionsWithNA
                )}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.systemsFeedDataSystem && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemsFeedDataSystem.message}
            </p>
          )}
        </div>

        {/* Department receives data */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which department receives data from this system?
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="deptReceiveData"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingSystems}
                options={dataSystemsOptions}
                placeholder="Select department(s)..."
                onChange={(selected) =>
                  handleSelectChange("deptReceiveData", selected)
                }
                value={getSelectValue("deptReceiveData", dataSystemsOptions)}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.deptReceiveData && (
            <p className="text-red-500 text-xs mt-1">
              {errors.deptReceiveData.message}
            </p>
          )}
        </div>

        {/* System receives data */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which system receives data from this system?
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="deptSystemReceiveData"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                isLoading={loadingDeptSystems}
                options={deptSystemOptionsWithNA}
                placeholder="Select system(s)..."
                onChange={(selected) =>
                  handleSelectChange("deptSystemReceiveData", selected)
                }
                value={getSelectValue(
                  "deptSystemReceiveData",
                  deptSystemOptionsWithNA
                )}
                closeMenuOnSelect={false}
              />
            )}
          />
          {errors.deptSystemReceiveData && (
            <p className="text-red-500 text-xs mt-1">
              {errors.deptSystemReceiveData.message}
            </p>
          )}
        </div>

        {/* Reports produced */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            What reports are produced for decision-making?
            <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register("reportProduced")}
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical ${
              errors.reportProduced ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="E.g. Commercial-billing system-customers numbers"
          />
          {errors.reportProduced && (
            <p className="text-red-500 text-xs mt-1">
              {errors.reportProduced.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
          >
            Go Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-12 py-2.5 bg-[#2070B8] text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DataFlowEdit;
